import {
  Alert,
  Box,
  Dialog,
  IconButton,
  Skeleton,
  Slider,
  Stack,
  Typography,
} from "@mui/material";
import { Close } from "@mui/icons-material";
import { useMemo, useState } from "react";
import { formattedDate } from "../../utils/dateUtils";
import {
  getBoundingBoxStyle,
  getDetectionConfidence,
} from "../../utils/fieldDataDiseaseStatusUtils";

const DetectionBox = ({ detection }) => {
  const boxStyle = getBoundingBoxStyle(detection);
  const confidence = getDetectionConfidence(detection);

  if (!boxStyle) {
    return null;
  }

  return (
    <Box
      sx={{
        position: "absolute",
        border: "2px solid #e53935",
        boxShadow: "0 0 0 1px rgba(255,255,255,0.9)",
        pointerEvents: "none",
        ...boxStyle,
      }}
    >
      <Box
        sx={{
          position: "absolute",
          left: 0,
          top: "-24px",
          bgcolor: "#e53935",
          color: "white",
          px: 0.75,
          py: 0.25,
          fontSize: "11px",
          lineHeight: 1.2,
          whiteSpace: "nowrap",
        }}
      >
        {detection.classification}
        {confidence !== null ? ` ${Math.round(confidence * 100)}%` : ""}
      </Box>
    </Box>
  );
};

const DiseaseImageDetailDialog = ({
  open,
  onClose,
  detail,
  isLoading,
  error,
}) => {
  const image = detail?.image;
  const detections = useMemo(
    () => detail?.detections || [],
    [detail?.detections],
  );
  const [confidenceThreshold, setConfidenceThreshold] = useState(70);
  const visibleDetections = useMemo(
    () =>
      detections.filter((detection) => {
        const confidence = getDetectionConfidence(detection);

        return confidence === null || confidence * 100 >= confidenceThreshold;
      }),
    [confidenceThreshold, detections],
  );
  const handleConfidenceThresholdChange = (_, value) => {
    setConfidenceThreshold(Array.isArray(value) ? value[0] : value);
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      aria-labelledby="disease-image-detail-title"
      sx={{
        "& .MuiDialog-paper": {
          borderRadius: "4px",
          overflow: "hidden",
        },
      }}
    >
      <Box display="flex" flexDirection="column" gap={2} p={2}>
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="flex-start"
          gap={2}
        >
          <Box>
            <Typography
              id="disease-image-detail-title"
              fontWeight="bold"
              fontSize="20px"
            >
              {image?.classification || "Dettaglio foto"}
            </Typography>
            {image?.timestamp && (
              <Typography fontSize="13px" color="text.secondary">
                {formattedDate(image.timestamp, true)}
              </Typography>
            )}
          </Box>
          <IconButton aria-label="close" onClick={onClose}>
            <Close sx={{ width: 20, height: 20 }} />
          </IconButton>
        </Stack>

        {error && (
          <Alert severity="error">
            Errore durante il caricamento della foto. Riprova più tardi.
          </Alert>
        )}

        {isLoading ? (
          <Skeleton variant="rectangular" width="100%" height={420} />
        ) : image?.imageBase64 ? (
          <Box>
            {detections.length > 0 && (
              <Box mb={1}>
                <Stack
                  direction="row"
                  alignItems="center"
                  justifyContent="space-between"
                  gap={2}
                >
                  <Typography fontSize="13px" fontWeight="bold">
                    Fiducia minima
                  </Typography>
                  <Typography fontSize="13px" color="text.secondary">
                    {confidenceThreshold}%
                  </Typography>
                </Stack>
                <Slider
                  value={confidenceThreshold}
                  onChange={handleConfidenceThresholdChange}
                  min={0}
                  max={100}
                  step={5}
                  size="small"
                  sx={{
                    color: "#216557",
                    mt: 0.5,
                  }}
                />
              </Box>
            )}
            <Box
              width="100%"
              sx={{
                bgcolor: "#0f1714",
                borderRadius: "4px",
                overflow: "hidden",
                display: "flex",
                justifyContent: "center",
              }}
            >
              <Box
                position="relative"
                sx={{
                  display: "inline-block",
                  maxWidth: "100%",
                }}
              >
                <Box
                  component="img"
                  src={image.imageBase64}
                  alt={image.imageName || "Foto rilevazione"}
                  sx={{
                    display: "block",
                    maxWidth: "100%",
                    maxHeight: "65vh",
                    objectFit: "contain",
                  }}
                />
                {visibleDetections.map((detection, index) => (
                  <DetectionBox
                    key={`${detection.id || detection.classification}-${index}`}
                    detection={detection}
                  />
                ))}
              </Box>
            </Box>
            {detections.length === 0 && (
              <Typography fontSize="13px" color="text.secondary" mt={1}>
                Nessun riquadro disponibile per questa immagine.
              </Typography>
            )}
            {detections.length > 0 && visibleDetections.length === 0 && (
              <Typography fontSize="13px" color="text.secondary" mt={1}>
                Nessun riquadro supera la soglia selezionata.
              </Typography>
            )}
          </Box>
        ) : (
          <Alert severity="info">
            Foto non disponibile per questa rilevazione.
          </Alert>
        )}

        {image && (
          <Stack direction="column" spacing={0.75}>
            <Typography fontSize="13px" sx={{ wordBreak: "break-word" }}>
              <strong>File:</strong> {image.imageName}
            </Typography>
            <Typography fontSize="13px">
              <strong>Dimensioni:</strong> {image.width} x {image.height}px
            </Typography>
            <Typography fontSize="13px">
              <strong>Oggetti identificati:</strong>{" "}
              {detail?.identified_object_count ?? detections.length}
            </Typography>
          </Stack>
        )}
      </Box>
    </Dialog>
  );
};

export default DiseaseImageDetailDialog;
